import { useState, useEffect } from "react";
import { PracticeRepository } from "../infrastructure/repositories/PracticeRepository";
import { FolderRepository } from "../infrastructure/repositories/FolderRepository";
import { Practice } from "@/domain/entities/Practice";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Input } from "./ui/input";
import { toast } from "sonner"; 
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import type { DropResult } from "react-beautiful-dnd";
import { secureStorage } from "@/utils/secureStorage";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "./ui/tooltip";


import {PracticeSession ,PracticeType} from "../types";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "./ui/pagination";

import { 
  Plus, 
  FolderPlus, 
  BookOpen, 
  Calendar, 
  Target,
  Search,
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  Copy,
  ArrowLeft,
  Download
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdownMenu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

type PracticeContentProps = {
  onSectionChange: (section: string) => void;
};

export function PracticeContent({ onSectionChange }: PracticeContentProps) {
  const [practices, setPractices] = useState<PracticeSession[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    type: "all",
    dateRange: "all",
    yardageMin: "",
    yardageMax: "",
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [practicesPerPage, setPracticesPerPage] = useState(50); // 50 practices per page
  const [isLoading, setIsLoading] = useState(false);

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isCreateFolderDialogOpen, setIsCreateFolderDialogOpen] = useState(false);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [disableTooltips, setDisableTooltips] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [folderTooltipOpen, setFolderTooltipOpen] = useState(false);
  const [practiceTooltipOpen, setPracticeTooltipOpen] = useState(false);
  const [importTooltipOpen, setImportTooltipOpen] = useState(false);
  //  const userType = userData?.type === "coach" ? "coach" : "swimmer";
  type UserType = "swimmer" | "coach";
  const userType: UserType =
    secureStorage.get<UserType>("user_type") ?? "swimmer";
  
const [newPractice, setNewPractice] = useState({
    name: "",
    description: "",
     date: new Date().toISOString().split("T")[0],
    type: "workout" as const,
    practice_time : "AM",
    //time: "AM",
    course: "SCY", 
  });

  const parsePracticeDate = (dateStr: string): Date | null => {
  // ISO format (YYYY-MM-DD)
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
  }

  // US format (MM/DD/YYYY)
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
    const [month, day, year] = dateStr.split("/").map(Number);
    const d = new Date(year, month - 1, day, 12, 0, 0);
    return isNaN(d.getTime()) ? null : d;
  }

  // Fallback
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d;
};


  const [newFolder, setNewFolder] = useState({ name: "", description: "" });

  // Get user name and user ID from localStorage
  const selectedSwimmerName = localStorage.getItem("selected_swimmer_name") || secureStorage.get("current_user_name");
  const selectedSwimmerProfilePic = localStorage.getItem("selected_swimmer_pic") || secureStorage.get("current_user_pic");
 
  const userId =
    localStorage.getItem("selected_swimmer_id") ||
    localStorage.getItem("current_user_swimmer_id");

// Fetch practices with pagination
const fetchData = async () => {
  if (!userId) return;

  setIsLoading(true);
  let combined: PracticeSession[] = [];

  try {
      const recentLaps = [];
    const practiceRepo = new PracticeRepository();
    const folderRepo = new FolderRepository();

    const selectedSwimmerId = localStorage.getItem("selected_swimmer_id");
    let coachId: number;
    let swimmerId: number;

    if (selectedSwimmerId) {
      coachId = 0;
      swimmerId = Number(selectedSwimmerId);
    } else {
      coachId = Number(localStorage.getItem("coach_id")) || 0;
      swimmerId = Number(localStorage.getItem("current_user_swimmer_id")) || 0;
    }

   // const folderData = await folderRepo.getAllFoldersPaginated(swimmerId || coachId,currentPage, practicesPerPage);
  const [folderData, practiceData] = await Promise.all([
    folderRepo.getAllFoldersPaginated(swimmerId || coachId, currentPage, practicesPerPage),
    practiceRepo.findPaginated(userId, currentPage, practicesPerPage)
  ]);

  console.log(practiceData)
    const practiceToFolderMap: Record<string, string> = {};
    folderData.practices?.forEach((p: any) => {
      const modelId = String(p.folder_practice_model_id);
      const folderId = String(p.folder_id);
      if (modelId && folderId) {
        practiceToFolderMap[modelId] = folderId;
      }
    });

    const practiceYardageMap: Record<string, number> = {};
    folderData.laps?.forEach((lap: any) => {
      const pid = String(lap.folder_practice_model_id);
      const dist = parseInt(lap.distance) || 0;
      const reps = parseInt(lap.rep) || 1;
      if (!pid) return;
      if (!practiceYardageMap[pid]) practiceYardageMap[pid] = 0;
      practiceYardageMap[pid] += dist * reps;
    });

    const folderYardageMap: Record<string, number> = {};
    folderData.practices?.forEach((p: any) => {
      const fid = String(p.folder_id);
      const pid = String(p.folder_practice_model_id);
      if (!fid) return;
      const practiceYardage = practiceYardageMap[pid] || 0;
      if (!folderYardageMap[fid]) folderYardageMap[fid] = 0;
      folderYardageMap[fid] += practiceYardage;
    });

    const mappedFolders: PracticeSession[] = folderData.folders.map((f: any) => ({
      id: f.folder_id,
      date: f.createdAt
        ? new Date(f.createdAt).toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          })
        : "—",
      name: f.name || "Untitled Folder",
      yardage: folderYardageMap[f.folder_id] || 0,
      type: "folder",
      description: f.description || "",
      practice_time: "",
    }));

    const mappedFolderPractices: PracticeSession[] =
      folderData.practices?.map((p: any) => ({
          // id: p.folder_practice_id,
          id: p.folder_practice_id,                // UI id
          folder_practice_id: p.folder_practice_id,
          folder_practice_model_id: p.folder_practice_model_id,   // this is what backend uses 
            date: p.practice_date
                ? new Date(p.practice_date).toLocaleDateString("en-US", {
                    month: "2-digit",
                    day: "2-digit",
                    year: "numeric",
                  })
                : "—",
          name: p.name || "Unnamed Folder Practice",
          yardage: practiceYardageMap[p.folder_practice_model_id] || 0,
          type: "workout",
          description: p.description || "",
          practice_time: p.practice_time || "",
          folder_id: p.folder_id,
          courseTypeLookupId: Number(p.CourseTypeLookupId || p.practice_CourseTypeLookupId || 1),
        })) || [];

  const { practice = [], laps = [] } =
       (await practiceRepo.findByUserId(userId)) || {};

    const normalPracticeYardageMap: Record<string, number> = {};
    if (Array.isArray(laps)) {
      laps.forEach((lap: any) => {
        const pid = String(lap.practice_id);
        const dist = parseInt(lap.distance) || 0;
        const reps = parseInt(lap.rep) || 1;
        if (!pid) return;
        if (!normalPracticeYardageMap[pid]) normalPracticeYardageMap[pid] = 0;
        normalPracticeYardageMap[pid] += dist * reps;
      });
    }

    const mappedPractices: PracticeSession[] = practice.map((p: any) => {
      const pid = String(p.practice_id);
      return {
        id: pid,
        date: p.practice_date
          ? new Date(p.practice_date).toLocaleDateString("en-US", {
              month: "2-digit",
              day: "2-digit",
              year: "numeric",
            })
          : "Invalid Date",
        name: p.name?.trim() ? p.name : p.practice_time || "Unnamed Practice",
        yardage: normalPracticeYardageMap[pid] || 0,
        type: "workout",
        description: p.description || "",
        practice_time: p.practice_time || "",
        isFavorite: p.isFavorite == "1" ? true : false,
        courseTypeLookupId: Number(p.CourseTypeLookupId || p.practice_CourseTypeLookupId || 1),
      };
    });

    // Load drafts from localStorage
    const drafts = JSON.parse(localStorage.getItem("practice_drafts") || "[]");
    const mappedDrafts: PracticeSession[] = drafts.map((draft: any) => ({
      id: draft.id,
      date: draft.date || new Date().toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      }),
      name: draft.name || "Untitled Draft",
      yardage: draft.yardage || 0,
      type: "workout" as PracticeType,
      description: draft.description || "",
      practice_time: draft.practice_time || "",
      isDraft: true,
    }));

    combined = [...mappedDrafts, ...mappedFolders, ...mappedPractices].sort((a, b) => {
      if (a.type === "folder" && b.type !== "folder") return -1;
      if (b.type === "folder" && a.type !== "folder") return 1;
      if ((a as any).isDraft && !(b as any).isDraft) return -1;
      if (!(a as any).isDraft && (b as any).isDraft) return 1;
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });

  if (folderData?.laps && Array.isArray(folderData.laps)) {
    recentLaps.push(
      ...folderData.laps.map((lap: any) => ({
        practice_id: lap.folder_practice_model_id,
        distance: lap.distance,
        rep: lap.rep,
        stroke: lap.stroke || "",
        createdAt: lap.createdAt || ""
      }))
    );
  }

  // Laps from normal practices
  if (Array.isArray(laps)) {
    recentLaps.push(
      ...laps.map((lap: any) => ({
        practice_id: lap.practice_id,
        distance: lap.distance,
        rep: lap.rep,
        stroke: lap.stroke || "",
        createdAt: lap.createdAt || ""
      }))
    );
  }

  // Sort by date (if available) and keep last 20
  const sorted = recentLaps
    .sort((a: any, b: any) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )
    .slice(0, 20);

    setPractices(combined);
    setFolderPractices(mappedFolderPractices);

    localStorage.setItem("recent_laps", JSON.stringify(sorted));

  } catch (err) {
    console.error("Failed to fetch data:", err);
  } finally {
    if (combined.length > 0) {

      const now = new Date();

      const currentWeekStart = new Date(now);
      currentWeekStart.setDate(now.getDate() - now.getDay());
      const lastWeekStart = new Date(currentWeekStart);
      lastWeekStart.setDate(currentWeekStart.getDate() - 7);
      const lastWeekEnd = new Date(currentWeekStart);
      lastWeekEnd.setDate(currentWeekStart.getDate() - 1);

      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
      const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;

      let weeklyYardage = 0;
      let monthlyYardage = 0;
      let lastWeeklyYardage = 0;
      let lastMonthlyYardage = 0;

      combined.forEach((p: PracticeSession) => {
        const date = new Date(p.date);
        const yardage = p.yardage || 0;
        if (isNaN(date.getTime())) return; // skip invalid dates

        // Current week
        if (date >= currentWeekStart && date <= now) {
          weeklyYardage += yardage;
        }

        // Last week
        if (date >= lastWeekStart && date <= lastWeekEnd) {
          lastWeeklyYardage += yardage;
        }

        // Current month
        if (date.getMonth() === currentMonth && date.getFullYear() === currentYear) {
          monthlyYardage += yardage;
        }

        // Last month
        if (date.getMonth() === lastMonth && date.getFullYear() === lastMonthYear) {
          lastMonthlyYardage += yardage;
        }
      });

      localStorage.setItem("weekly_yardage", String(weeklyYardage));
      localStorage.setItem("monthly_yardage", String(monthlyYardage));
      localStorage.setItem("last_weekly_yardage", String(lastWeeklyYardage));
      localStorage.setItem("last_monthly_yardage", String(lastMonthlyYardage));
      
      console.log({
        weeklyYardage,
        lastWeeklyYardage,
        monthlyYardage,
        lastMonthlyYardage,
      });

      //  Save today's practice dynamically
      const today = new Date().toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });

      const todayPractice = combined.find(
        (p: any) => p.date === today && p.type === "workout"
      );

      if (todayPractice) {
        localStorage.setItem("today_practice", JSON.stringify(todayPractice));
      } else {
        localStorage.removeItem("today_practice");
      }
    }

    setIsLoading(false);
  }
};

useEffect(() => {
  fetchData();
}, [userId, currentPage, practicesPerPage]);


  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: string) => {
  if (practices.length === 0) return; 

  setPracticesPerPage(parseInt(newPageSize));
  setCurrentPage(1);
};

const handleExportPractice = async (practice: PracticeSession) => {
  try {
    const repo = new PracticeRepository();

    const selectedSwimmerId =
      localStorage.getItem("selected_swimmer_id") ||
      localStorage.getItem("current_user_swimmer_id");

    const swimmerId = Number(selectedSwimmerId || 0);

    const all = await repo.findByUserId(String(swimmerId));

    const laps = all?.laps?.filter(
      (l: any) => String(l.practice_id) === String(practice.id)
    ) || [];

    const exportObject = {
      id: practice.id,
      name: practice.name,
      description: practice.description,
      date: practice.date,
      practice_time: practice.practice_time,
      yardage: practice.yardage,
      laps: laps.map((l: any) => ({
        id: l.id,
        rep: l.rep,
        distance: l.distance,
        interval: l.interval_lap || l.interval || "0",
        energy_zone: l.energy_zone || "",
        stroke: l.stroke || ""
      }))
    };

    const jsonString = JSON.stringify(exportObject, null, 2);

    const blob = new Blob([jsonString], { type: "application/json" });

    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `${practice.name || "practice"}-${practice.id}.json`;
    a.click();

    URL.revokeObjectURL(url);

    toast.success("Practice exported");
  } catch (err) {
    console.error("Export failed", err);
    toast.error("Failed to export practice");
  }
};

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  const filteredPractices = (() => {
  const s = searchTerm.toLowerCase().trim();

  // If user is searching, de-duplicate practices by id so search results don't show duplicates
  const sourceList =
    s.length > 0
      ? Array.from(
          new Map(practices.map((p) => [p.id, p] as [string, PracticeSession]))
            .values()
        )
      : practices;

  return sourceList.filter((item) => {
    const matchesSearch =
      !s ||
      item.name.toLowerCase().includes(s) ||
      (item.description?.toLowerCase().includes(s)) ||
      item.date.toLowerCase().includes(s);

  const matchesType =
    filters.type === "all" || item.type === filters.type;

  const matchesYardage =
    (!filters.yardageMin || item.yardage >= Number(filters.yardageMin)) &&
    (!filters.yardageMax || item.yardage <= Number(filters.yardageMax));

  let matchesDate = true;

  if (filters.dateRange !== "all") {
    const itemDate = parsePracticeDate(item.date);
    if (!itemDate) return false;

    const now = new Date();
    now.setHours(23, 59, 59, 999);

    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    if (filters.dateRange === "today") {
      matchesDate =
        itemDate >= todayStart && itemDate <= now;
    }

    if (filters.dateRange === "week") {
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    const weekStart = new Date(today);
    const day = weekStart.getDay(); 
    const diff = day === 0 ? 6 : day - 1;
    weekStart.setDate(weekStart.getDate() - diff);
    weekStart.setHours(0, 0, 0, 0);

    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    matchesDate = itemDate >= weekStart && itemDate <= weekEnd;
  }

    if (filters.dateRange === "month") {
  const monthStart = new Date(
    now.getFullYear(),
    now.getMonth(),
    1,
    0, 0, 0, 0
  );

  const monthEnd = new Date(
    now.getFullYear(),
    now.getMonth() + 1,
    0,
    23, 59, 59, 999
  );

  matchesDate =
    itemDate >= monthStart && itemDate <= monthEnd;
}

}


  return matchesSearch && matchesType && matchesYardage && matchesDate;
  });
})();

const foldersOnly = filteredPractices.filter((p) => p.type === "folder");
const practicesOnly = filteredPractices.filter((p) => p.type !== "folder");

const totalFolders = foldersOnly.length;
const [activeFolder, setActiveFolder] = useState<PracticeSession | null>(null);
const [folderPractices, setFolderPractices] = useState<PracticeSession[]>([]);

const itemsToPaginate = activeFolder ? folderPractices : filteredPractices;

useEffect(() => {
  setTotalPages(Math.ceil(itemsToPaginate.length / practicesPerPage) || 1);
}, [itemsToPaginate, practicesPerPage, activeFolder]);

useEffect(() => {
  if (currentPage > totalPages) {
    setCurrentPage(totalPages);
  }
}, [currentPage, totalPages]);


const startIndex = (currentPage - 1) * practicesPerPage;
const endIndex = currentPage * practicesPerPage;

let pageItems: PracticeSession[] = [];
console.log(pageItems)
if (startIndex < totalFolders) {
  const folderSlice = foldersOnly.slice(startIndex, endIndex);
  const remaining = practicesPerPage - folderSlice.length;
  if (remaining > 0 && totalFolders < endIndex) {
    const practiceSlice = practicesOnly.slice(0, remaining);
    pageItems = [...folderSlice, ...practiceSlice];
  } else {
    pageItems = folderSlice;
  }
} else {
  const practiceStart = startIndex - totalFolders;
  const practiceEnd = endIndex - totalFolders;
  pageItems = practicesOnly.slice(practiceStart, practiceEnd);
}

const handleDuplicate = async (practice: PracticeSession) => {
  try {
    if (practice.isDraft) {
      toast.error("Draft practices cannot be duplicated");
      return;
    }

    const repo = new PracticeRepository();
    const selectedSwimmerId = localStorage.getItem("selected_swimmer_id");

    let coachId: number;
    let swimmerId: number;

    if (selectedSwimmerId) {
      coachId = 0;
      swimmerId = Number(selectedSwimmerId);
    } else {
      coachId = Number(localStorage.getItem("coach_id")) || 0;
      swimmerId = Number(localStorage.getItem("current_user_swimmer_id")) || 0;
    }

    const newId =
      (Math.max(0, ...practices.map(p => Number(p.id) || 0)) + 1).toString();

    const allData = await repo.findByUserId(String(swimmerId));
    const oldLaps =
      allData?.laps?.filter(
        (l: any) => String(l.practice_id) === String(practice.id)
      ) || [];

    const duplicatedLaps = oldLaps.map((lap: any) => ({
      ...lap,
      id: 0,
      practice_lap_id: 0,
      practice_id: newId,
      interval: lap.interval_lap || lap.interval || "0",
      rep: lap.rep || "1",
      practiceId: undefined
    }));

    const duplicateModel = Practice.create({
      id: newId,
      practiceId: newId,
      coachId,
      swimmerId,
      name: practice.name,
      description: practice.description ?? '',
      date: new Date(),
      practice_time: practice.practice_time || "AM",
      courseTypeLookupId: 1,
      isFavorite: false,
      isFinished: false,
      imageData: null
    });

    await repo.save(coachId, swimmerId, duplicateModel, duplicatedLaps);

    toast.success("Duplicated practice successfully");

    // IMPORTANT: update UI only after success
    await fetchData();

  } catch (error) {
    console.error("Failed to duplicate practice:", error);
    toast.error("Failed to duplicate practice");
  }
};


// Check if no swimmer is selected
if (!userId) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 mx-auto bg-orange-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-2xl font-semibold text-gray-900">No Swimmer Selected</h2>
          <p className="text-gray-600 max-w-md">
            Please set current swimmer first to see all practice sessions and training data.
          </p>
          <div className="pt-4 space-x-3">
            <button 
              onClick={() => window.location.reload()} 
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh Page
            </button>
            <button 
              onClick={() => {
                // Navigate to settings or swimmer selection
                const event = new CustomEvent('navigate', { detail: 'settings' });
                window.dispatchEvent(event);
              }}
              className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Go to Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const totalPractices = practices.length;
const totalYardage = practices.reduce((sum, p) => sum + (p.yardage || 0), 0);
const avgYardagePerPractice = totalPractices > 0 ? totalYardage / totalPractices : 0;

// Keep your same formatter
const formatYardage = (yardage: number) => yardage.toLocaleString();

const getTypeColor = (type: string, isDraft?: boolean) => {
  if (isDraft) return "bg-yellow-100 text-yellow-800 border-yellow-200";
  switch (type) {
    case "program": return "bg-blue-100 text-blue-800 border-blue-200";
    case "workout": return "bg-green-100 text-green-800 border-green-200";
    case "set": return "bg-orange-100 text-orange-800 border-orange-200";
    default: return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

  const [isSubmitting, setIsSubmitting] = useState(false);


  // Create a new practice locally (you can also send it to the API)
 const handleCreatePractice = async () => {
  try {
      if (isSubmitting) return; 
      setIsSubmitting(true);
   
   const numericIds = practices
      .map(p => Number(p.id))
      .filter(n => !isNaN(n) && n > 0);

    const newId = (numericIds.length > 0
      ? Math.max(...numericIds) + 1
      : 1
    ).toString();

   const repo = new PracticeRepository();
  // const selectedSwimmerId = localStorage.getItem("current_user_swimmer_id");
  const selectedSwimmerId = localStorage.getItem("selected_swimmer_id") ?? localStorage.getItem("current_user_swimmer_id");

    let coachId: number;
    let swimmerId: number;

    if (selectedSwimmerId) {
      coachId = 0;
      swimmerId = Number(selectedSwimmerId);
    } else {
      coachId = Number(localStorage.getItem("coach_id")) || 0;
      swimmerId = Number(localStorage.getItem("current_user_swimmer_id")) || 0;
    }
      console.log('selected swimmer id', selectedSwimmerId);
      console.log('selected coach id', coachId)

    const practiceModel = Practice.create({
        id: newId,
        practiceId: newId,
        coachId,
        swimmerId,
        name: newPractice.name.trim(),
        description: newPractice.description,
        date: new Date(newPractice.date),
        practice_time: newPractice.practice_time,
        courseTypeLookupId:
          newPractice.course === "SCY"
            ? 1
            : newPractice.course === "SCM"
            ? 2
            : 3,
        isFavorite: false,
        isFinished: false,
        imageData: null,
        folderId: activeFolder ? activeFolder.id : null
      });

    let savedPractice;
     ;
    if (activeFolder) {
      savedPractice = await repo.storeFolderPractice(
        coachId,
        swimmerId,
        practiceModel, 
        []
      );
    } else {
      savedPractice = await repo.save(coachId, swimmerId, practiceModel, []);
    }

    const practiceSession = {
      id: savedPractice.id,
      date: new Date(savedPractice.date).toISOString().split("T")[0],
      name: savedPractice.name,
      yardage: 0,
      type: "workout" as PracticeType,
      description: savedPractice.description,
      practice_time: savedPractice.practice_time,
    };

    if (activeFolder) {
      setFolderPractices((prev) => [practiceSession, ...prev]);
    } else {
      setPractices((prev) => [practiceSession, ...prev]);
    }

    toast.success("Practice created successfully!");

    setNewPractice({
      name: "",
      description: "",
      date: new Date().toISOString().split("T")[0],
      type: "workout",
      practice_time: "AM",
      course: "SCY",
    });
    setIsCreateDialogOpen(false);

  } catch (error) {
    console.error("Failed to create practice:", error);
    toast.error("Failed to create practice. Please try again.");
  }finally{
    // setIsLoading(false);
      setIsSubmitting(false);
  }
};

  const handleCreateFolder = async () => {
    if (!newFolder.name.trim()) return;

    const repo = new FolderRepository();
    const practiceRepo = new PracticeRepository();
    const selectedSwimmerId = localStorage.getItem("selected_swimmer_id");

    let coachId: number;
    let swimmerId: number;

    if (selectedSwimmerId) {
      coachId = 0;
      swimmerId = Number(selectedSwimmerId);
    } else {
      coachId = Number(localStorage.getItem("coach_id")) || 0;
      swimmerId = Number(localStorage.getItem("current_user_swimmer_id")) || 0;
    }

    try {
      if (isSubmitting) return;
      setIsSubmitting(true);

      if (isEditMode && practiceToEdit?.type === "folder") {
        const oldFolderId = practiceToEdit.id;

        // Get practices in the folder
        const folderPracticesRes = await repo.getPracticesByFolderId(Number(oldFolderId));
        const oldPractices = folderPracticesRes || [];

        // Get all folder data to fetch laps
        const allFoldersData = await repo.getAllFolders(swimmerId || coachId);
        const allLaps = allFoldersData.laps || [];

        // Create a set of practice IDs in this folder
        const practiceIdSet = new Set(
          oldPractices.map((p: any) =>
            String(
              p.id ??
              p.folder_practice_model_id ??
              p.practice_id ??
              p.folderPracticeModelId
            )
          )
        );

        // Filter laps that belong to practices in this folder
        const folderLaps = allLaps.filter((lap: any) => {
          const rawPracticeId =
            lap.practice_id ??
            lap.practiceId ??
            lap.folder_practice_model_id ??
            lap.folder_practice_id ??
            lap.folderPracticeModelId ??
            lap.folderPracticeId;

          if (!rawPracticeId) return false;
          return practiceIdSet.has(String(rawPracticeId));
        });

        // Group laps by practice ID
        const lapsByPracticeId: Record<string, any[]> = {};
        folderLaps.forEach((lap: any) => {
          const rawPracticeId =
            lap.practice_id ??
            lap.practiceId ??
            lap.folder_practice_model_id ??
            lap.folder_practice_id ??
            lap.folderPracticeModelId ??
            lap.folderPracticeId;

          if (!rawPracticeId) return;
          const pid = String(rawPracticeId);
          if (!lapsByPracticeId[pid]) lapsByPracticeId[pid] = [];
          lapsByPracticeId[pid].push(lap);
        });

        // Store practices with their laps
        const practicesWithLaps = oldPractices.map((p: any) => {
          const practiceId =
            String(
              p.id ??
              p.folder_practice_model_id ??
              p.practice_id ??
              p.folderPracticeModelId
            );
          return {
            practice: p,
            laps: lapsByPracticeId[practiceId] || [],
          };
        });

        localStorage.setItem("temp_folder_practices", JSON.stringify(practicesWithLaps));

        await repo.deleteFolder(Number(oldFolderId));

        const newFolderId = crypto.randomUUID();
        const createdFolder = await repo.manageFolder(
          coachId,
          swimmerId,
          newFolderId,
          newFolder.name,
          newFolder.description,
          false
        );

        const restored = JSON.parse(localStorage.getItem("temp_folder_practices") || "[]");

        for (const item of restored) {
          const p = item.practice;
          const laps = item.laps || [];

          // Generate new practice model ID
          const newModelId = crypto.randomUUID();

          // Normalize laps for folder practice (matching the format used in movePracticeToFolder)
          const normalizedLaps = laps.map((lap: any) => ({
            createdAt: lap.createdAt ?? lap.createdAtString ?? new Date().toISOString(),
            description: lap.description ?? "",
            distance: lap.distance?.toString() ?? "0",
            energy_zone: lap.energy_zone ?? "",
            folder_practice_lap_id: "0",
            folder_practice_model_id: newModelId,
            interval: lap.interval_lap ?? lap.interval ?? "0",
            isnoEditable: lap.isNoEditable ?? (lap.isnoEditable === "1" ? "1" : "0"),
            passedRapNumber: lap.passedRepNumber ?? lap.passedRapNumber ?? "0",
            rep: lap.rep?.toString() ?? "1",
          }));

          // Parse the original date
          const originalDate = p.date
            ? new Date(p.date)
            : new Date();

          const newPracticeModel = Practice.create({
            id: newModelId,
            practiceId: newModelId,
            coachId,
            swimmerId,
            name: p.name,
            description: p.description,
            date: originalDate,
            practice_time: p.practice_time || "AM",
            courseTypeLookupId: 1, // Default to SCY, you may want to preserve this if available
            isFavorite: false,
            isFinished: false,
            imageData: null,
            folderId: createdFolder.folder_id,
          });

          // Use storeFolderPractice instead of save for folder practices
          await practiceRepo.storeFolderPractice(
            coachId,
            swimmerId,
            newPracticeModel,
            normalizedLaps
          );
        }

        localStorage.removeItem("temp_folder_practices");

        setPractices((prev) =>
          prev.map((item) =>
            item.id === oldFolderId
              ? {
                  ...item,
                  id: createdFolder.folder_id,
                  name: newFolder.name,
                  description: newFolder.description,
                }
              : item
          )
        );

        toast.success("Folder updated successfully!");
        await fetchData(); // Refresh data to show updated folder with practices

      }

      else {
        const newFolderId = crypto.randomUUID();

        const folder = await repo.manageFolder(
          coachId,
          swimmerId,
          newFolderId,
          newFolder.name,
          newFolder.description,
          false
        );

        const newFolderItem: PracticeSession = {
          id: folder.folder_id || newFolderId,
          date: new Date().toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          }),
          name: newFolder.name,
          yardage: 0,
          type: "folder",
          description: newFolder.description,
          practice_time: "",
        };

        setPractices((prev) => [newFolderItem, ...prev]);
        toast.success("Folder created successfully!");
      }

      setNewFolder({ name: "", description: "" });
      setIsEditMode(false);
      setPracticeToEdit(null);
      setIsCreateFolderDialogOpen(false);

    } catch (error) {
      console.error("Failed to create/update folder:", error);
      toast.error("Failed to save folder.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to convert courseTypeLookupId to course string
  const getCourseFromLookupId = (lookupId?: number): "SCY" | "SCM" | "LCM" => {
    if (lookupId === 2) return "SCM";
    if (lookupId === 3) return "LCM";
    return "SCY"; // Default to SCY (1)
  };

  const normalizeToISODate = (dateStr: string) => {

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
      const [month, day, year] = dateStr.split("/").map(Number);
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }

    return new Date().toISOString().split("T")[0];
  };

const handleEdit = (item: PracticeSession) => {
  if (item.type === "folder") {
    setNewFolder({
      name: item.name,
      description: item.description || "",
    });

    setIsEditMode(true);
    setPracticeToEdit(item);
    setIsCreateFolderDialogOpen(true);
    return;
  }

  // Practice edit
  setIsEditMode(true);
  setPracticeToEdit(item);

  setNewPractice({
    name: item.name,
    description: item.description || "",
    date: normalizeToISODate(item.date),
    type: "workout",
    practice_time: item.practice_time || "AM",
    course: getCourseFromLookupId(item.courseTypeLookupId),
  });

  setIsCreateDialogOpen(true);
};


  const [isEditMode, setIsEditMode] = useState(false);
  const [practiceToEdit, setPracticeToEdit] = useState<PracticeSession | null>(null);

  const handleUpdatePractice = async () => {
  if (!practiceToEdit) return;

  try {
    setIsSubmitting(true);

    const repo = new PracticeRepository();
    const selectedSwimmerId = localStorage.getItem("selected_swimmer_id");

    let coachId: number;
    let swimmerId: number;

    if (selectedSwimmerId) {
      coachId = 0;
      swimmerId = Number(selectedSwimmerId);
    } else {
      coachId = Number(localStorage.getItem("coach_id")) || 0;
      swimmerId = Number(localStorage.getItem("current_user_swimmer_id")) || 0;
    }

    const oldId = practiceToEdit.id;

    const wasFavorite = Boolean(practiceToEdit.isFavorite);

    const allData = await repo.findByUserId(String(swimmerId));

    const existingLaps =
      allData?.laps?.filter(
        (l: any) => String(l.practice_id) === String(oldId)
      ) || [];

    const newId = (
      Math.max(0, ...practices.map((p) => Number(p.id) || 0)) + 1
    ).toString();

    const normalizedLaps = existingLaps.map((lap: any) => ({
      ...lap,
      id: 0,
      practice_lap_id: 0,
      practice_id: newId,
      interval: lap.interval_lap || lap.interval || "0",
      rep: lap.rep || "1",
      practiceId: undefined,
    }));

    await repo.delete(oldId, swimmerId);

    const newModel = Practice.create({
      id: newId,
      practiceId: newId,
      coachId,
      swimmerId,
      name: newPractice.name.trim(),
      description: newPractice.description,
      date: new Date(newPractice.date),
      practice_time: newPractice.practice_time,
      courseTypeLookupId:
        newPractice.course === "SCY"
          ? 1
          : newPractice.course === "SCM"
          ? 2
          : 3,
      isFavorite: wasFavorite, // IMPORTANT
      isFinished: false,
      imageData: null,
      folderId: activeFolder ? activeFolder.id : undefined,
    });

    const saved = await repo.save(
      coachId,
      swimmerId,
      newModel,
      normalizedLaps
    );

    const newPracticeItem: PracticeSession = {
      id: saved.id,
      date: new Date(saved.date).toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      }),
      name: saved.name,
      yardage: 0,
      type: "workout",
      description: saved.description,
      practice_time: saved.practice_time,
      isFavorite: wasFavorite,
    };

    setPractices((prev) => [
      newPracticeItem,
      ...prev.filter((p) => p.id !== oldId),
    ]);

    toast.success("Practice updated successfully");

    setIsCreateDialogOpen(false);
    setIsEditMode(false);
    setPracticeToEdit(null);

  } catch (error) {
    console.error("Failed to update practice:", error);
    toast.error("Failed to update practice");
  } finally {
    setIsSubmitting(false);
    fetchData();
  }
};


const applySearchAndFilters = (list: PracticeSession[]) => {
  const s = searchTerm.toLowerCase().trim();

  return list.filter((item) => {
    const matchesSearch =
      !s ||
      item.name.toLowerCase().includes(s) ||
      item.description?.toLowerCase().includes(s) ||
      item.date.toLowerCase().includes(s);

    const matchesType =
      filters.type === "all" || item.type === filters.type;

    const matchesYardage =
      (!filters.yardageMin || item.yardage >= Number(filters.yardageMin)) &&
      (!filters.yardageMax || item.yardage <= Number(filters.yardageMax));

    let matchesDate = true;

    if (filters.dateRange !== "all") {
      const itemDate = new Date(item.date);
      const now = new Date();

      if (filters.dateRange === "today") {
        matchesDate =
          itemDate.toDateString() === now.toDateString();
      }

      if (filters.dateRange === "week") {
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        matchesDate =
          itemDate >= weekAgo && itemDate <= now;
      }

      if (filters.dateRange === "month") {
        const startOfMonth = new Date(
          now.getFullYear(),
          now.getMonth(),
          1,
          0, 0, 0, 0
        );

        const endOfMonth = new Date(
          now.getFullYear(),
          now.getMonth() + 1,
          0,
          23, 59, 59, 999
        );

        matchesDate =
          itemDate >= startOfMonth &&
          itemDate <= endOfMonth;
      }
    }

    return matchesSearch && matchesType && matchesYardage && matchesDate;
  });
};


const visiblePractices = activeFolder
  ? applySearchAndFilters(folderPractices)
  : filteredPractices;

const handleDeleteItem = async (item: PracticeSession) => {
  console.log('item', item);
  const isFolderPractice =
    Boolean((item as any).folder_practice_id) ||
    Boolean((item as any).folder_practice_model_id);

  console.log('isFolderPractice', isFolderPractice)
  console.log('item' , item);

  if (item.isDraft) {
    const drafts = JSON.parse(localStorage.getItem("practice_drafts") || "[]");
    const updatedDrafts = drafts.filter((d: any) => d.id !== item.id);
    localStorage.setItem("practice_drafts", JSON.stringify(updatedDrafts));
    setPractices((prev) => prev.filter((p) => p.id !== item.id));
    toast.success("Draft deleted");
    return;
  }

  const itemId = item.id;
  const itemType = item.type;

  setPractices((prev) => prev.filter((p) => p.id !== itemId));

  try {
    setIsLoading(true);

    // ------------------------------
    // FOLDER DELETE
    // ------------------------------
    if (itemType === "folder") {
      const folderRepo = new FolderRepository();
      await folderRepo.deleteFolder(Number(itemId));
      toast.success("Folder deleted");
      return;
    }

    // ------------------------------
    // PRACTICE DELETE
    // ------------------------------
    const selectedSwimmerId = localStorage.getItem("selected_swimmer_id");
    const swimmerId = selectedSwimmerId
      ? Number(selectedSwimmerId)
      : Number(localStorage.getItem("current_user_swimmer_id")) || 0;

      let folderPracticeId: string | null = null;

      if (activeFolder) {
      const fp = folderPractices.find(p => p.id === item.id);
      folderPracticeId =
        fp?.folder_practice_id ??
        fp?.folder_practice_model_id ??
        null;
      } else {
      folderPracticeId =
        (item as any).folder_practice_id ??
        (item as any).folder_practice_model_id ??
        null;
      }
      
    // ------------------------------
    // FOLDER PRACTICE DELETE
    // ------------------------------
    if (isFolderPractice == true) {
      console.log('went to delete folder practice')
      const folderRepo = new FolderRepository();

      if (!folderPracticeId) {
        throw new Error("Folder practice ID missing");
      }

      await folderRepo.deleteFolderPractice(
        String(item.folder_practice_model_id)
      );

      toast.success("Practice removed from folder");
    }
    else {
      const practiceRepo = new PracticeRepository();
      await practiceRepo.delete(itemId, swimmerId);
      toast.success("Practice deleted");
    }


    await fetchData();

    const laps = JSON.parse(localStorage.getItem("recent_laps") || "[]");
    const filtered = laps.filter(
      (lap: any) => String(lap.practice_id) !== String(itemId)
    );
    localStorage.setItem("recent_laps", JSON.stringify(filtered));

  } catch (error) {
    console.error("Delete failed:", error);
    toast.error("Failed to delete");
    await fetchData();
  } finally {
    setIsLoading(false);
  }
};

  const handleApplyFilters = () => setIsFilterDialogOpen(false);
  const handleClearFilters = () => setFilters({ type: "all", dateRange: "all", yardageMin: "", yardageMax: "" });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [practiceToDelete, setPracticeToDelete] = useState<PracticeSession | null>(null);

  const handleOpenFolder = async (folder: PracticeSession) => {
  try {

  setSearchTerm("");
    setFilters({
      type: "all",
      dateRange: "all",
      yardageMin: "",
      yardageMax: "",
    });
    setCurrentPage(1);

    setIsLoading(true);
    const folderRepo = new FolderRepository();

    const practices = await folderRepo.getPracticesByFolderId(Number(folder.id));
    console.log("Fetched folder practices", { folderId: folder.id, practices });
    const allFoldersData = await folderRepo.getAllFolders(
      Number(localStorage.getItem("selected_swimmer_id")) || 
      Number(localStorage.getItem("current_user_swimmer_id"))
    );
    console.log("Fetched folder aggregate data", {
      folderId: folder.id,
      lapsCount: allFoldersData.laps?.length || 0,
      practicesCount: practices.length,
    });

    const lapArray = allFoldersData.laps || [];
    const practiceYardageMap: Record<string, number> = {};

    const practiceIdSet = new Set(
      practices.map(
        (p: any) =>
          String(
            p.id ??
              p.folder_practice_model_id ??
              p.practice_id ??
              p.folderPracticeModelId
          )
      )
    );

    lapArray.forEach((lap: any) => {
      const rawPracticeId =
        lap.practice_id ??
        lap.practiceId ??
        lap.folder_practice_model_id ??
        lap.folder_practice_id ??
        lap.folderPracticeModelId ??
        lap.folderPracticeId;

      if (!rawPracticeId) return;

      const pid = String(rawPracticeId);
      if (!practiceIdSet.has(pid)) return;

      const dist = parseInt(lap.distance) || 0;
      const reps = parseInt(lap.rep) || 1;

      if (!practiceYardageMap[pid]) practiceYardageMap[pid] = 0;
      practiceYardageMap[pid] += dist * reps;
    });

    const mappedPractices: PracticeSession[] = practices.map((p: any) => {
      const practiceId =
        p.id ??
        p.folder_practice_model_id ??
        p.practice_id ??
        p.folderPracticeModelId;

      const computedYardage =
        practiceYardageMap[String(practiceId)] ??
        Number(p.yardage ?? p.total_yardage ?? p.totalYardage ?? 0) ??
        0;

      const mapped: PracticeSession = {
        id: p.folder_practice_id ?? p.id,

        folder_practice_id: p.folder_practice_id,
        folder_practice_model_id: p.folder_practice_model_id,
        folder_id: p.folder_id,

        name: p.name,
        description: p.description,
        date: p.date,
        type: "workout",
        yardage: computedYardage,
        practice_time: p.practice_time || "",
        courseTypeLookupId: Number(
          p.CourseTypeLookupId || p.practice_CourseTypeLookupId || 1
        ),
      };

      return mapped;
    });

    setFolderPractices(mappedPractices);
    setActiveFolder(folder);
  } catch (err) {
    console.error("Failed to load folder practices:", err);
    toast.error("Failed to load folder practices.");
  } finally {
    setIsLoading(false);
  }
};

const handleDragEnd = (result: DropResult) => {
  const { draggableId, destination } = result;

  if (!destination) return;

  const practiceId = draggableId.replace("practice-", "");

  if (!destination.droppableId.startsWith("folder-")) {
    return;
  }

  const folderId = destination.droppableId.replace("folder-", "");

  movePracticeToFolder(practiceId, folderId);
};

const movePracticeToFolder = async (practiceId: string, folderId: string) => {
  try {

    const practiceRepo = new PracticeRepository();

    const selectedSwimmerId = localStorage.getItem("selected_swimmer_id");
    const swimmerId = selectedSwimmerId
      ? Number(selectedSwimmerId)
      : Number(localStorage.getItem("current_user_swimmer_id")) || 0;

    const coachId = selectedSwimmerId ? 0 : Number(localStorage.getItem("coach_id")) || 0;

    const allData = await practiceRepo.findByUserId(String(swimmerId));
  
    const practice = allData.practice.find(
      (p: any) => String(p.practice_id) === String(practiceId)
    );

    if (!practice) {
      console.error("Practice not found, aborting.");
      toast.error("Practice not found");
      return;
    }

    const laps = allData.laps.filter(
      (l: any) => String(l.practice_id) === String(practiceId)
    );


    const newModelId = crypto.randomUUID();

    // This lap shape matches EXACT JSON that worked in Postman
    const normalizedLaps = laps.map((lap: any) => {
      const finalLap = {
        createdAt: lap.createdAt ?? lap.createdAtString ?? new Date().toISOString(),
        description: lap.description ?? "",
        distance: lap.distance?.toString() ?? "0",
        energy_zone: lap.energy_zone ?? "",
        folder_practice_lap_id: "0",
        folder_practice_model_id: newModelId,
        interval: lap.interval_lap ?? lap.interval ?? "0",
        isnoEditable: lap.isNoEditable ?? "0",
        passedRapNumber: lap.passedRepNumber ?? "0",
        rep: lap.rep?.toString() ?? "1"
      };

      return finalLap;
    });


    const newPracticeModel = Practice.create({
      id: newModelId,
      practiceId: newModelId,
      coachId,
      swimmerId,
      name: practice.name ?? "",
      description: practice.description ?? "",
      date: new Date(),
      practice_time: practice.practice_time ?? "AM",
      courseTypeLookupId: Number(practice.CourseTypeLookupId) || 1,
      folderId: folderId,
      isFavorite: false,
      isFinished: false,
      imageData: null
    });

    await practiceRepo.storeFolderPractice(
      coachId,
      swimmerId,
      newPracticeModel,
      normalizedLaps
    );

    await practiceRepo.delete(practiceId, swimmerId);

    toast.success("Practice moved to folder with laps");
    fetchData();

  } catch (err) {
    console.error("Failed to move practice with laps:", err);
    toast.error("Failed to move practice");
  }
};
const parseMMDDYYYY = (dateStr: string) => {
  const [month, day, year] = dateStr.split("/").map(Number);
  return new Date(year, month - 1, day, 12, 0, 0);
};

const handleImportPractice = async (event: React.ChangeEvent<HTMLInputElement>) => {
  try {
    const file = event.target.files?.[0];
    if (!file) return;

    const text = await file.text();
    let json = JSON.parse(text);

    const practicesArray = Array.isArray(json) ? json : [json];

    const repo = new PracticeRepository();

    const selectedSwimmerId =
      localStorage.getItem("selected_swimmer_id") ||
      localStorage.getItem("current_user_swimmer_id");

    const swimmerId = Number(selectedSwimmerId || 0);
    const coachId = Number(localStorage.getItem("coach_id")) || 0;

    let importedCount = 0;
    let failedCount = 0;

    for (const practiceJson of practicesArray) {
      try {
        const numericIds = practices
          .map(p => Number(p.id))
          .filter(n => !isNaN(n) && n > 0);

        const newPracticeId = (
          numericIds.length > 0
            ? Math.max(...numericIds) + 1
            : 1
        ).toString();

        const practiceModel = Practice.create({
          id: newPracticeId,
          practiceId: newPracticeId,
          coachId,
          swimmerId,
          name: practiceJson.name || "Imported Practice",
          description: practiceJson.description || "",
          date: practiceJson.date
                  ? parseMMDDYYYY(practiceJson.date)
                  : new Date(),
          practice_time: practiceJson.practice_time || "AM",
          courseTypeLookupId: 1,
          isFavorite: false,
          isFinished: false,
          imageData: null,
          folderId: activeFolder ? activeFolder.id : null
        });

        const normalizedLaps = (practiceJson.laps || []).map((lap: any) => ({
          id: 0,
          practice_lap_id: 0,
          practice_id: newPracticeId,

          rep: Number(lap.rep || 1),
          distance: Number(lap.distance || 0),
          interval: Number(lap.interval || 0),
          energy_zone: lap.energy_zone || "",
          description: lap.description || "",

          time: Number(lap.time || 0),

          passedRepNumber: Number(
            lap.passedRepNumber ?? lap.passedRapNumber ?? 0
          ),

          isNoEditable: lap.isNoEditable === true || lap.isnoEditable === "1",

          isPassed: Number(lap.isPassed || 0),

          user_id: String(swimmerId),
          coach_id: String(coachId),

          createdAt: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }));

        const saved = await repo.save(
          coachId,
          swimmerId,
          practiceModel,
          normalizedLaps
        );

        const newPracticeItem: PracticeSession = {
          id: saved.id,
          date: new Date(saved.date).toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric"
          }),
          name: saved.name,
          yardage: 0,
          type: "workout",
          description: saved.description,
          practice_time: saved.practice_time
        };

        if (activeFolder) {
          setFolderPractices(prev => [newPracticeItem, ...prev]);
        } else {
          setPractices(prev => [newPracticeItem, ...prev]);
        }

        importedCount++;
      } catch (innerErr) {
        console.error("Failed to import one practice:", innerErr);
        failedCount++;
      }
    }

    if (importedCount > 0) {
      toast.success(`Imported ${importedCount} practice(s) successfully`);
    }
    if (failedCount > 0) {
      toast.error(`${failedCount} practice(s) failed to import`);
    }

    event.target.value = "";
    fetchData();

  } catch (err) {
    console.error("Import failed:", err);
    toast.error("Invalid JSON or failed to import");
  }
};
  return (
    <div className="space-y-6">
      {/* Header with User Profile */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Avatar className="h-16 w-16">
            <AvatarImage src={selectedSwimmerProfilePic || undefined} />
            <AvatarFallback className="text-lg">
              {selectedSwimmerName
                ? selectedSwimmerName
                    .split(" ")            
                    .slice(0, 2)           
                    .map(word => word[0])  
                    .join("")              
                    .toUpperCase()         
                : "?"}
            </AvatarFallback>
          </Avatar>

          <div>
            <h1 className="text-3xl mb-1">{selectedSwimmerName}</h1>
            {userType === "coach" && (
              <p className="text-muted-foreground">
                Coach & Swimming Instructor
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Summary Stats - Moved to top */}
      {practices.length > 0 && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Practices</p>
                                    <p className="text-2xl font-semibold">{practices.length}</p>
                </div>
                <BookOpen className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Yardage</p>
                      <p className="text-2xl font-semibold">{formatYardage(Math.round(totalYardage))}</p>
                </div>
                <Target className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Avg per Practice</p>
                  <p className="text-2xl font-semibold">
                  {formatYardage(Math.round(avgYardagePerPractice))}
                </p>
                </div>
                <Calendar className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search, Filter, and Create Actions - All in one line */}
      <div className="flex items-center space-x-4">
        {/* Search Bar - Takes remaining space */}
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search practices by name or date..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Filter Button */}
        <Dialog open={isFilterDialogOpen} onOpenChange={(open) => {
          setIsFilterDialogOpen(open);
          if (!open) {
            setDisableTooltips(true);
            setTooltipOpen(false);
            setTimeout(() => setDisableTooltips(false), 300);
          }
        }}>
          <Tooltip
              open={disableTooltips ? false : tooltipOpen}
              onOpenChange={(open) => {
                if (!disableTooltips) setTooltipOpen(open);
              }}
            >
              <TooltipTrigger
                asChild
                onMouseEnter={() => setTooltipOpen(true)}
                onMouseLeave={() => setTooltipOpen(false)}
              >
                <DialogTrigger asChild>
                  <Button variant="outline" size="icon">
                    <Filter className="h-4 w-4" />
                  </Button>
                </DialogTrigger>
              </TooltipTrigger>

              <TooltipContent side="bottom">
                Filter practices
              </TooltipContent>
          </Tooltip>


          <DialogContent>
            <DialogHeader>
              <DialogTitle>Filter Practices</DialogTitle>
              <DialogDescription>
                Filter your practices by type, date range, and yardage
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="filter-type">Practice Type</Label>
                <select
                  id="filter-type"
                  value={filters.type}
                  onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="all">All Types</option>
                  <option value="program">Program</option>
                  <option value="workout">Workout</option>
                  <option value="set">Set</option>
                </select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="filter-date">Date Range</Label>
                <select
                  id="filter-date"
                  value={filters.dateRange}
                  onChange={(e) => setFilters({ ...filters, dateRange: e.target.value })}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="all">All Dates</option>
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="yardage-min">Min Yardage</Label>
                  <Input
                      id="yardage-min"
                      type="number"
                      min={0}
                      step={1}
                      value={filters.yardageMin}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          yardageMin: e.target.value,
                        })
                      }
                      placeholder="0"
                    />

                </div>
                <div className="space-y-2">
                  <Label htmlFor="yardage-max">Max Yardage</Label>
                  <Input
                      id="yardage-max"
                      type="number"
                      min={0}
                      step={1}
                      value={filters.yardageMax}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          yardageMax: e.target.value,
                        })
                      }
                      placeholder="10000"
                    />

                </div>
              </div>
              
              <div className="flex space-x-2 pt-4">
                <Button variant="outline" onClick={handleClearFilters}>
                  Clear Filters
                </Button>
                <Button onClick={handleApplyFilters}>
                  Apply Filters
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Create Folder Button */}
        <Dialog
              open={isCreateFolderDialogOpen}
              onOpenChange={(open) => {
                setIsCreateFolderDialogOpen(open);
                if (!open) {
                  // RESET STATES HERE
                  setIsEditMode(false);
                  setPracticeToEdit(null);
                  setNewFolder({ name: "", description: "" });
                  setDisableTooltips(true);
                  setFolderTooltipOpen(false);
                  setTimeout(() => setDisableTooltips(false), 300);
                }
              }}
            >
          <Tooltip
            open={disableTooltips ? false : folderTooltipOpen}
            onOpenChange={(open) => {
              if (!disableTooltips) {
                setFolderTooltipOpen(open);
              }
            }}
          >
            <TooltipTrigger
              asChild
              onMouseEnter={() => setFolderTooltipOpen(true)}
              onMouseLeave={() => setFolderTooltipOpen(false)}
            >
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                  size="icon"
                >
                  <FolderPlus className="h-4 w-4" />
                </Button>
              </DialogTrigger>
            </TooltipTrigger>

            <TooltipContent side="bottom">
              Create Folder
            </TooltipContent>
          </Tooltip>

          <DialogContent>
            <DialogHeader>
             <DialogTitle>{isEditMode ? "Edit Folder" : "Create New Folder"}</DialogTitle>

              <DialogDescription>
                Organize your practices into folders for better management
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="folder-name">Folder Name</Label>
                <Input
                    id="folder-name"
                    value={newFolder.name}
                    onChange={(e) => {
                      let value = e.target.value;
                      value = value.replace(/^\s+/, "");
                      if (value.trim().length > 0 || value === "") {
                        setNewFolder({ ...newFolder, name: value });
                      }
                    }}
                    placeholder="Enter folder name..."
                    maxLength={50}
                    required
                  />

              </div>
              <div className="space-y-2">
                <Label htmlFor="folder-description">Description</Label>
                <Textarea
                  id="folder-description"
                  value={newFolder.description}
                  onChange={(e) => setNewFolder({ ...newFolder, description: e.target.value })}
                  placeholder="Describe the folder purpose..."
                  rows={3}
                />
              </div>
              <div className="flex space-x-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setIsCreateFolderDialogOpen(false);
                  setIsEditMode(false);
                  setPracticeToEdit(null);
                  setNewFolder({ name: "", description: "" });

                  setDisableTooltips(true);
                  setFolderTooltipOpen(false);

                  setTimeout(() => {
                    setDisableTooltips(false);
                  }, 300);
                }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>

  <Button
    onClick={async () => {
      if (isSubmitting) return;
      setIsSubmitting(true);

      try {
        await handleCreateFolder();
      } finally {
        setIsSubmitting(false);
      }
    }}
    disabled={!newFolder.name || isSubmitting}
  >
    {isSubmitting ? (
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
        {isEditMode ? "Updating..." : "Creating..."}
      </div>
    ) : (
      isEditMode ? "Update Folder" : "Create Folder"
    )}
  </Button>
</div>

            </div>
          </DialogContent>
        </Dialog>
        
        {/* Create Practice Button */}
        <Dialog
              open={isCreateDialogOpen}
              onOpenChange={(open) => {
                setIsCreateDialogOpen(open);
                if (open && !isEditMode) {
                  // Reset form when opening for new practice (not editing)
                  setIsEditMode(false);
                  setPracticeToEdit(null);
                  setNewPractice({
                    name: "",
                    description: "",
                    date: new Date().toISOString().split("T")[0],
                    type: "workout",
                    practice_time: "AM",
                    course: "SCY"
                  });
                }
                if (!open) {
                  setIsEditMode(false);
                  setPracticeToEdit(null);
                  setNewPractice({
                    name: "",
                    description: "",
                    date: new Date().toISOString().split("T")[0],
                    type: "workout",
                    practice_time: "AM",
                    course: "SCY"
                  });
                  setDisableTooltips(true);
                  setPracticeTooltipOpen(false);
                  setTimeout(() => setDisableTooltips(false), 300);
                }
              }}
            >
          <Tooltip
            open={disableTooltips ? false : practiceTooltipOpen}
            onOpenChange={(open) => {
              if (!disableTooltips) {
                setPracticeTooltipOpen(open);
              }
            }}
          >
            <TooltipTrigger
              asChild
              onMouseEnter={() => setPracticeTooltipOpen(true)}
              onMouseLeave={() => setPracticeTooltipOpen(false)}
            >
              <DialogTrigger asChild>
                <Button
                  className="bg-green-600 hover:bg-green-700 text-white"
                  size="icon"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </DialogTrigger>
            </TooltipTrigger>

            <TooltipContent side="bottom">
              Create Practice
            </TooltipContent>
          </Tooltip>


  <DialogContent>
    <DialogHeader>
  <DialogTitle>{isEditMode ? "Edit Practice" : "Create New Practice"}</DialogTitle>
  <DialogDescription>
    {isEditMode
      ? "Update the details of this practice session."
      : "Add a new practice session to your training library."}
  </DialogDescription>
    </DialogHeader>

    <div className="space-y-4 py-4">
      {/* Practice Name */}
      <div className="space-y-2">
        <Label htmlFor="name">Practice Name</Label>
        <Input
          id="name"
          value={newPractice.name}
         onChange={(e) => {
            let value = e.target.value;
            value = value.replace(/^\s+/, "");
            if (value.trim().length > 0 || value === "") {
              setNewPractice({ ...newPractice, name: value });
            }
          }}

          placeholder="Enter practice name..."
          maxLength={60} 
          required
        />
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={newPractice.description}
          onChange={(e) => setNewPractice({ ...newPractice, description: e.target.value })}
          placeholder="Describe the practice session..."
          maxLength={500} 
          rows={3}
        />
      </div>

      {/* Date */}
      <div className="space-y-2">
        <Label htmlFor="date">Date</Label>
        <Input
          id="date"
          type="date"
          value={newPractice.date || new Date().toISOString().split("T")[0]}
          onChange={(e) => setNewPractice({ ...newPractice, date: e.target.value })}
          className="w-full"
        />
      </div>

      {/* Time Toggle (AM/PM) */}
      <div className="space-y-2">
        <Label>Time</Label>
        <div className="flex items-center space-x-2">
          {["AM", "PM"].map((option) => (
            <Button
              key={option}
              type="button"
              variant={newPractice.practice_time === option ? "default" : "outline"}
              onClick={() => setNewPractice({ ...newPractice, practice_time: option })}
              className={`w-1/2 ${newPractice.practice_time === option ? "bg-primary text-white hover:bg-primary" : ""}`}
            >
              {option}
            </Button>
          ))}
        </div>
      </div>

      {/* Choose Course */}
      <div className="space-y-2">
        <Label>Course</Label>
        <div className="flex items-center space-x-2">
          {["SCY", "SCM", "LCM"].map((course) => (
            <Button
              key={course}
              type="button"
              variant={newPractice.course === course ? "default" : "outline"}
              onClick={() => setNewPractice({ ...newPractice, course })}
              className={`flex-1 ${newPractice.course === course ? "bg-primary text-white hover:bg-primary" : ""}`}
            >
              {course}
            </Button>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex space-x-2 pt-4">
  <Button
  variant="outline"
  onClick={() => {
    // Close dialog
    setIsCreateDialogOpen(false);

    // Reset edit state
    setIsEditMode(false);
    setPracticeToEdit(null);

    // Reset form
    setNewPractice({
      name: "",
      description: "",
      date: new Date().toISOString().split("T")[0],
      type: "workout",
      practice_time: "AM",
      course: "SCY"
    });

    // IMPORTANT: reset tooltip state
    setDisableTooltips(true);
    setPracticeTooltipOpen(false);

    setTimeout(() => {
      setDisableTooltips(false);
    }, 300);
  }}
  disabled={isSubmitting}
>
  Cancel
</Button>



  <Button
    onClick={async () => {
      if (isSubmitting) return;
      setIsSubmitting(true);

      try {
        if (isEditMode) {
          await handleUpdatePractice();
        } else {
          await handleCreatePractice();
        }
      } finally {
        setIsSubmitting(false);
      }
    }}
    disabled={!newPractice.name || isSubmitting}
  >
    {isSubmitting ? (
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
        {isEditMode ? "Updating..." : "Creating..."}
      </div>
    ) : (
      isEditMode ? "Update Practice" : "Create Practice"
    )}
  </Button>
</div>

    </div>
  </DialogContent>
        </Dialog>

        <Tooltip
          open={disableTooltips ? false : importTooltipOpen}
          onOpenChange={(open) => {
            if (!disableTooltips) {
              setImportTooltipOpen(open);
            }
          }}
        >
          <TooltipTrigger
            asChild
            onMouseEnter={() => setImportTooltipOpen(true)}
            onMouseLeave={() => setImportTooltipOpen(false)}
          >
            <Button
              variant="outline"
              size="icon"
              className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
              onClick={() => {
                setDisableTooltips(true);
                setImportTooltipOpen(false);
                setTimeout(() => setDisableTooltips(false), 300);
                document.getElementById("importPracticeInput")?.click();
              }}
            >
              <Download className="h-4 w-4" />
            </Button>
          </TooltipTrigger>

          <TooltipContent side="bottom">
            Import Practice
          </TooltipContent>
        </Tooltip>

        <input
          id="importPracticeInput"
          type="file"
          accept="application/json"
          className="hidden"
          onChange={handleImportPractice}
        />
        
      </div>

      {/* Practice Book Table */}
    <DragDropContext onDragEnd={handleDragEnd}>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            {!activeFolder && (
              <>
                <CardTitle className="flex items-center">
                  <BookOpen className="mr-2 h-5 w-5" />
                  Practice Book
                </CardTitle>

                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <Label htmlFor="page-size" className="text-sm font-medium">
                      Show:
                    </Label>
                    <Select
                      value={practicesPerPage.toString()}
                      onValueChange={handlePageSizeChange}
                    >
                      <SelectTrigger id="page-size" className="w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                        <SelectItem value="150">150</SelectItem>
                        <SelectItem value="200">200</SelectItem>
                        <SelectItem value="300">300</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}

            {activeFolder && (
              <div className="flex items-center space-x-3">
                <Button
                  variant="ghost"
                   onClick={() => {
                        setActiveFolder(null);
                        setSearchTerm("");
                        setFilters({
                          type: "all",
                          dateRange: "all",
                          yardageMin: "",
                          yardageMax: "",
                        });
                      }}
                  className="flex items-center space-x-2"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>

                <CardTitle className="flex items-center">
                  <FolderPlus className="mr-2 h-5 w-5 text-blue-600" />
                  {activeFolder.name}
                </CardTitle>
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent>
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            </div>
          ) : (
            <>
              {/* Desktop View */}
              <div className="hidden md:block">

                {/* Header Row */}
                <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-muted/50 rounded-lg border">
                  <div className="col-span-2 font-medium flex items-center">
                    <Calendar className="mr-2 h-4 w-4" />
                    Date
                  </div>
                  <div className="col-span-5 font-medium">Name</div>
                  <div className="col-span-2 font-medium text-right">Yardage</div>
                  <div className="col-span-2 font-medium">Type</div>
                  <div className="col-span-1 font-medium text-center">Actions</div>
                </div>

                {/* MAIN LIST DROPPABLE */}
                <Droppable droppableId="main-list" type="practice">
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className="space-y-2 mt-2"
                    >

                      {visiblePractices.map(
                        (practice, index) => {

                          if (practice.type === "folder") {
                            return (
                              <Droppable
                                key={`folder-${practice.id}`}
                                droppableId={`folder-${practice.id}`}
                                type="practice"
                              >
                                {(folderProvided, snapshot) => (
                                  <div
                                    ref={folderProvided.innerRef}
                                    {...folderProvided.droppableProps}
                                    className={`grid grid-cols-12 gap-4 px-4 py-4 border rounded-lg 
                                      transition-colors cursor-pointer
                                      ${
                                        snapshot.isDraggingOver
                                          ? "bg-blue-50"
                                          : "hover:bg-muted/40"
                                      }`}
                                    onClick={() => handleOpenFolder(practice)}
                                  >
                                    <div className="col-span-2 flex items-center">
                                      <FolderPlus className="text-blue-600 h-4 w-4" />
                                      <span className="ml-2 text-sm font-medium">
                                        {practice.date}
                                      </span>
                                    </div>

                                    <div className="col-span-5 min-w-0">
                                      <p className="font-medium truncate">
                                        {practice.name}
                                      </p>
                                      {practice.description && (
                                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                          {practice.description}
                                        </p>
                                      )}
                                    </div>

                                    <div className="col-span-2 flex items-center justify-end">
                                      <Badge variant="outline">
                                        {formatYardage(practice.yardage || 0)}
                                      </Badge>
                                    </div>

                                    <div className="col-span-2 flex items-center">
                                      <Badge
                                        className={`${getTypeColor(
                                          practice.type,
                                          (practice as any).isDraft
                                        )} capitalize`}
                                      >
                                        {(practice as any).isDraft ? "Draft" : practice.type}
                                      </Badge>
                                    </div>

                                    <div className="col-span-1 flex items-center justify-center">
                                      <DropdownMenu>
                                        <DropdownMenuTrigger
                                          onClick={(e) => e.stopPropagation()}
                                          asChild
                                        >
                                          <Button variant="ghost" size="sm">
                                            <MoreHorizontal className="h-4 w-4" />
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                          <DropdownMenuItem
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleEdit(practice);
                                            }}
                                          >
                                            <Edit className="mr-2 h-4 w-4" />
                                            Edit Folder
                                          </DropdownMenuItem>

                                          <DropdownMenuSeparator />

                                          <DropdownMenuItem
                                            className="text-destructive"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setPracticeToDelete(practice);
                                              setIsDeleteDialogOpen(true);
                                            }}
                                          >
                                            <Trash2 className="mr-2 h-4 w-4" />
                                            Delete Folder
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    </div>

                                    {folderProvided.placeholder}
                                  </div>
                                )}
                              </Droppable>
                            );
                          }

                          return (
                            <Draggable
                              key={`practice-${practice.id}`}
                              draggableId={`practice-${practice.id}`}
                              index={index}
                            >
                              {(dragProvided, snapshot) => (
                                <div
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  {...dragProvided.dragHandleProps}
                                  className={`grid grid-cols-12 gap-4 px-4 py-4 border rounded-lg 
                                    transition-colors cursor-pointer
                                    ${
                                      snapshot.isDragging
                                        ? "bg-gray-100 shadow-md"
                                        : "hover:bg-muted/40"
                                    }`}
                                  onClick={(e) => {
                                          // Prevent opening if clicking within dropdown menu
                                          const target = e.target as HTMLElement;
                                          if (target.closest('[role="menu"]') || 
                                              target.closest('[data-radix-dropdown-menu-content]') ||
                                              target.closest('[data-disabled="true"]') || 
                                              target.closest('[aria-disabled="true"]') ||
                                              target.closest('[role="menuitem"][aria-disabled="true"]')) {
                                            return;
                                          }
                                          
                                          // Handle draft practices differently
                                          if ((practice as any).isDraft) {
                                            const drafts = JSON.parse(localStorage.getItem("practice_drafts") || "[]");
                                            const draft = drafts.find((d: any) => d.id === practice.id);
                                            
                                            if (draft) {
                                              const selectedPractice = {
                                                ...practice,
                                                practiceId: practice.id,
                                                id: practice.id,
                                                sets: draft.sets || [],
                                                isDraft: true,
                                              };

                                              localStorage.setItem(
                                                "selected_practice",
                                                JSON.stringify(selectedPractice)
                                              );

                                              onSectionChange("sets");
                                            }
                                            return;
                                          }

                                          const selectedPractice = {
                                            ...practice,

                                            // IMPORTANT: use model id for laps
                                            practiceId:
                                              (practice as any).folder_practice_model_id ??
                                              practice.id,

                                            id:
                                              (practice as any).folder_practice_model_id ??
                                              practice.id,
                                          };

                                          localStorage.setItem(
                                            "selected_practice",
                                            JSON.stringify(selectedPractice)
                                          );

                                          onSectionChange("sets");
                                        }}
                                >
                                  <div className="col-span-2 flex items-center">
                                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                    <span className="ml-2 text-sm font-medium">
                                      {practice.date}
                                    </span>
                                  </div>

                                  <div className="col-span-5 min-w-0">
                                    <p className="font-medium break-words">
                                      {practice.name}
                                    </p>

                                    {practice.description && (
                                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2 break-words">
                                        {practice.description}
                                      </p>
                                    )}
                                  </div>


                                  <div className="col-span-2 flex items-center justify-end">
                                    <Badge variant="outline">
                                      {formatYardage(practice.yardage || 0)}
                                    </Badge>
                                  </div>

                                    <div className="col-span-2 flex items-center">
                                      <Badge
                                        className={`${getTypeColor(
                                          practice.type,
                                          (practice as any).isDraft
                                        )} capitalize`}
                                      >
                                        {(practice as any).isDraft ? "Draft" : practice.type}
                                      </Badge>
                                    </div>

                                  <div className="col-span-1 flex items-center justify-center">
                                    <DropdownMenu>
                                      <DropdownMenuTrigger
                                        onClick={(e) => e.stopPropagation()}
                                        asChild
                                      >
                                        <Button variant="ghost" size="sm">
                                          <MoreHorizontal className="h-4 w-4" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        <DropdownMenuItem
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEdit(practice);
                                          }}
                                        >
                                          <Edit className="mr-2 h-4 w-4" />
                                          Edit Practice
                                        </DropdownMenuItem>

                                        <DropdownMenuItem
                                            disabled={(practice as any).isDraft}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              e.preventDefault();
                                              if ((practice as any).isDraft) return;
                                              handleDuplicate(practice);
                                            }}
                                            onMouseDown={(e) => {
                                              if ((practice as any).isDraft) {
                                                e.stopPropagation();
                                                e.preventDefault();
                                              }
                                            }}
                                          >
                                            <Copy className="mr-2 h-4 w-4" />
                                            Duplicate Practice
                                          </DropdownMenuItem>


                                          <DropdownMenuItem
                                            disabled={(practice as any).isDraft}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              e.preventDefault();
                                              if ((practice as any).isDraft) return;
                                              handleExportPractice(practice);
                                            }}
                                            onMouseDown={(e) => {
                                              if ((practice as any).isDraft) {
                                                e.stopPropagation();
                                                e.preventDefault();
                                              }
                                            }}
                                          >
                                            <Copy className="mr-2 h-4 w-4" />
                                            Export Practice
                                          </DropdownMenuItem>

                                        <DropdownMenuSeparator />

                                        <DropdownMenuItem
                                          className="text-destructive"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setPracticeToDelete(practice);
                                            setIsDeleteDialogOpen(true);
                                          }}
                                        >
                                          <Trash2 className="mr-2 h-4 w-4" />
                                          Delete Practice
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          );
                        }
                      )}

                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>

              {/* Empty State */}
              {(activeFolder ? folderPractices : filteredPractices).length === 0 && (
                <div className="text-center py-12">
                  <BookOpen className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No practices found</h3>
                  <p className="text-muted-foreground mb-4">
                    {searchTerm
                      ? "Try adjusting your search terms"
                      : "Create your first practice to get started"}
                  </p>
                  {!searchTerm && (
                    <Button onClick={() => {
                      setIsEditMode(false);
                      setPracticeToEdit(null);
                      setNewPractice({
                        name: "",
                        description: "",
                        date: new Date().toISOString().split("T")[0],
                        type: "workout",
                        practice_time: "AM",
                        course: "SCY"
                      });
                      setIsCreateDialogOpen(true);
                    }}>
                      <Plus className="mr-2 h-4 w-4" /> Create Practice
                    </Button>
                  )}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <Pagination>
                  <PaginationContent>

                    <PaginationItem>
                      <PaginationPrevious
                        onClick={() =>
                          currentPage > 1 && handlePageChange(currentPage - 1)
                        }
                        className={
                          currentPage === 1
                            ? "pointer-events-none opacity-50"
                            : "cursor-pointer"
                        }
                      />
                    </PaginationItem>

                    {(() => {
                      const pages = [];
                      const maxVisible = 5;
                      let start = Math.max(1, currentPage - 2);
                      let end = Math.min(totalPages, start + maxVisible - 1);
                      if (end - start + 1 < maxVisible) {
                        start = Math.max(1, end - maxVisible + 1);
                      }

                      for (let i = start; i <= end; i++) {
                        pages.push(
                          <PaginationItem key={i}>
                            <PaginationLink
                              onClick={() => handlePageChange(i)}
                              isActive={currentPage === i}
                              className="cursor-pointer"
                            >
                              {i}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      }
                      return pages;
                    })()}

                    {currentPage < totalPages - 2 && totalPages > 5 && (
                      <PaginationItem>
                        <PaginationEllipsis />
                      </PaginationItem>
                    )}

                    <PaginationItem>
                      <PaginationNext
                        onClick={() =>
                          currentPage < totalPages &&
                          handlePageChange(currentPage + 1)
                        }
                        className={
                          currentPage === totalPages
                            ? "pointer-events-none opacity-50"
                            : "cursor-pointer"
                        }
                      />
                    </PaginationItem>

                  </PaginationContent>
                </Pagination>
              )}
            </>
          )}
        </CardContent>

      </Card>
    </DragDropContext>


 {/* Delete Dialog */}
    <Dialog
      open={isDeleteDialogOpen}
      onOpenChange={setIsDeleteDialogOpen}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete</DialogTitle>
          <DialogDescription className="break-words text-balance">
            Are you sure you want to delete
            <span className="font-semibold break-all line-clamp-2">
              {practiceToDelete?.name}
            </span>
            ?
          </DialogDescription>
        </DialogHeader>

        <div className="flex justify-end space-x-3 mt-6">
          <Button
            variant="outline"
            onClick={() => setIsDeleteDialogOpen(false)}
          >
            Cancel
          </Button>

          <Button
            variant="destructive"
            onClick={() => {
              if (practiceToDelete) {
                handleDeleteItem(practiceToDelete);
                setIsDeleteDialogOpen(false);
              }
            }}
          >
            Delete
          </Button>
        </div>
      </DialogContent>
    </Dialog>

 </div>
);
}